import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    Renderer2,
    RendererStyleFlags2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { TimeoutService } from '@df/ng/timeout.service';
import { NToggle } from '@df/ui/toggle/toggle.service';
import { BehaviorSubject } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { StateReaderService } from '../toggle/state-reader.service';
import { NgClass } from '@angular/common';

let uid = 1;

@Component({
    standalone: true,
    selector: 'hide-up, [hideUp]',
    templateUrl: 'hide-up.html',
    styleUrl: './hide-up.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass]
})
export class HideUpComponent implements OnChanges, OnDestroy, AfterViewInit {
    readonly uid = `hideup${uid++}`;

    @Input()
    set hideUp(value: BehaviorSubject<boolean> | NToggle.TGroupId | NToggle.TId | boolean) {
        this.open = value;

        this.openHandler();
    }

    /**
     * If it should open up and not down
     */
    @Input()
    hideUpOpenUp = false;

    /**
     * Toggling element
     */
    @Input()
    open: BehaviorSubject<boolean> | NToggle.TGroupId | NToggle.TId | boolean = false;

    /**
     * Content element
     */
    @ViewChild('content', { read: ElementRef })
    private content?: ElementRef<HTMLElement>;

    /**
     * Normalized open entry to behavior subject
     */
    private _open?: BehaviorSubject<boolean>;

    /**
     * open subscription
     */
    private openSubscription?: Subscription;

    /**
     * Height subscription
     */
    private heightSubscirption?: Subscription;

    /**
     * Last applied animate class
     */
    private animateApplied = false;

    /**
     * Animation drop timeout id
     */
    private timeoutId?: number;

    /**
     * Dependencies
     */
    constructor(
        private stateReaderService: StateReaderService,
        private renderer2: Renderer2,
        private timeoutService: TimeoutService,
        private elementRef: ElementRef
    ) {}

    /**
     * On changes hook
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['open']) {
            this.openHandler();
        }
    }

    /**
     * Listen to height changes
     */

    ngAfterViewInit() {
        const content = this.content?.nativeElement;
        if (content) {
            this.heightSubscirption = resizeObservable(content, entry => this.updateHeight(entry.contentRect.height)).subscribe();
        }
    }

    private updateHeight(height: number) {
        this.renderer2.setStyle(this.elementRef.nativeElement, '--hide-up-height', `${height}px`, RendererStyleFlags2.DashCase);
    }

    /**
     * Cleanup
     */
    ngOnDestroy() {
        this.clear();
        this.openSubscription?.unsubscribe();
        this.heightSubscirption?.unsubscribe();
    }

    private openHandler() {
        this.openSubscription?.unsubscribe();
        this._open = this.stateReaderService.toState<boolean>(this.open);
        this.openSubscription = this._open.subscribe(() => this.update({ animate: true }));
        this.update({ animate: false });
    }

    /**
     * Update all needed things
     */
    private update(options: { animate: boolean }): void {
        this.setAnimate(options.animate, () => this._open && this.setOpen(this._open.getValue()));
    }

    /**
     * Enable or disable animation for transition
     */
    private setAnimate(animate: boolean, cb?: () => void) {
        if (this.animateApplied !== animate) {
            this.animateApplied = animate;
            const el = this.elementRef.nativeElement;
            animate ? this.renderer2.addClass(el, '_animate') : this.renderer2.removeClass(el, '_animate');
        }
        if (cb) {
            animate ? this.timeoutService.setTimeout(cb, 50) : cb();
        }
        this.clear();
    }

    /**
     * Set element height
     */
    private setOpen(open: boolean) {
        const el = this.elementRef.nativeElement;
        open ? this.renderer2.addClass(el, '_open') : this.renderer2.removeClass(el, '_open');
    }

    private clear() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        if (this.animateApplied) {
            this.timeoutId = this.timeoutService.setTimeout(() => this.setAnimate(false), 500);
        }
    }
}
